<template>
  <div>
    <v-card outlined class="employee-form-section rounded-xl mb-5">
      <v-card-title class="employee-form-section__title">
        <v-icon color="primary" class="mr-3">mdi-account-outline</v-icon>
        <div>
          <div>Employee details</div>
          <div class="employee-form-section__subtitle">Identity, contact information, and register access</div>
        </div>
      </v-card-title>
      <v-divider />
      <v-card-text class="pb-1">
        <v-row>
          <v-col cols="12" md="6">
            <v-text-field v-model="employee.firstName" outlined label="First name" autocomplete="given-name"
              :error-messages="errors.firstName" @blur="$emit('touch', 'firstName')" />
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field v-model="employee.lastName" outlined label="Last name" autocomplete="family-name"
              :error-messages="errors.lastName" @blur="$emit('touch', 'lastName')" />
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field v-model="employee.phoneNumber" outlined type="tel" label="Phone number" autocomplete="tel"
              :error-messages="errors.phoneNumber" @blur="$emit('touch', 'phoneNumber')" />
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field v-model="employee.email" outlined type="email" label="Email address" autocomplete="email"
              :error-messages="errors.email" @blur="$emit('touch', 'email')" />
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field v-model="employee.pinCode" outlined maxlength="255"
              :type="showCredential ? 'text' : 'password'"
              label="Register PIN or internal password"
              hint="At least 4 characters; letters, numbers, and symbols are supported."
              persistent-hint autocomplete="new-password" :error-messages="errors.pinCode"
              @blur="$emit('touch', 'pinCode')">
              <template #append>
                <v-btn icon small :aria-label="showCredential ? 'Hide credential' : 'Show credential'"
                  :aria-pressed="String(showCredential)" @click="showCredential = !showCredential">
                  <v-icon>{{ showCredential ? 'mdi-eye-off-outline' : 'mdi-eye-outline' }}</v-icon>
                </v-btn>
              </template>
            </v-text-field>
          </v-col>
          <v-col v-if="showEmployeeTypes" cols="12" md="6" class="pt-md-5">
            <div class="text-subtitle-2 mb-1">Employee type</div>
            <div class="d-flex flex-wrap">
              <v-checkbox v-if="showDriver" v-model="employee.metadata.isDriver" class="mt-0 mr-5"
                label="Driver" hide-details />
              <v-checkbox v-if="showTechnician" v-model="employee.metadata.isTechnician" class="mt-0"
                label="Technician" hide-details />
            </div>
          </v-col>
          <v-col cols="12">
            <v-textarea v-model="employee.note" outlined label="Internal notes" auto-grow rows="2"
              hint="Optional notes for administrators" />
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-card outlined class="employee-form-section rounded-xl mb-5">
      <v-card-title class="employee-form-section__title">
        <v-icon color="primary" class="mr-3">mdi-map-marker-outline</v-icon>
        <div>
          <div>Address</div>
          <div class="employee-form-section__subtitle">Optional home or mailing address</div>
        </div>
      </v-card-title>
      <v-divider />
      <v-card-text class="pb-1">
        <v-row>
          <v-col cols="12" md="6">
            <v-text-field v-model="employee.Address.addressLine1" outlined label="Street address"
              autocomplete="address-line1" :error-messages="errors.addressLine1" @blur="$emit('touch', 'addressLine1')" />
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field v-model="employee.Address.addressLine2" outlined label="Apartment, suite, or unit"
              autocomplete="address-line2" />
          </v-col>
          <v-col cols="12" sm="6" md="4">
            <v-text-field v-model="employee.Address.city" outlined label="City" autocomplete="address-level2"
              :error-messages="errors.city" @blur="$emit('touch', 'city')" />
          </v-col>
          <v-col cols="12" sm="6" md="4">
            <v-autocomplete v-model="employee.Address.StateId" outlined label="State" :items="states"
              item-text="name" item-value="id" autocomplete="address-level1" />
          </v-col>
          <v-col cols="12" sm="6" md="4">
            <v-text-field v-model="employee.Address.postalCode" outlined label="ZIP / postal code"
              autocomplete="postal-code" />
          </v-col>
          <v-col cols="12" sm="6" md="4">
            <v-autocomplete v-model="employee.Address.CountryCode" outlined label="Country" :items="countries"
              item-text="name" item-value="code" autocomplete="country" />
          </v-col>
          <v-col cols="12" sm="6" md="4">
            <v-text-field v-model="employee.Address.phoneNumber" outlined type="tel" label="Address phone"
              autocomplete="tel" />
          </v-col>
          <v-col cols="12" sm="6" md="4">
            <v-text-field v-model="employee.Address.type" outlined label="Address type" hint="Home, office, or other" />
          </v-col>
          <v-col cols="12">
            <v-textarea v-model="employee.Address.description" outlined label="Address notes" auto-grow rows="2" />
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
export default {
  name: "EmployeeFormFields",
  props: {
    employee: { type: Object, required: true },
    states: { type: Array, default: () => [] },
    countries: { type: Array, default: () => [] },
    errors: { type: Object, default: () => ({}) },
    showDriver: Boolean,
    showTechnician: Boolean,
  },
  data: () => ({ showCredential: false }),
  computed: {
    showEmployeeTypes() {
      return this.showDriver || this.showTechnician;
    },
  },
};
</script>

<style scoped>
.employee-form-section { background: var(--app-surface, #fff); }
.employee-form-section__title { font-size: 1.05rem; line-height: 1.35; padding: 18px 20px; }
.employee-form-section__subtitle { color: var(--app-text-muted, rgba(0, 0, 0, 0.6)); font-size: 0.8125rem; font-weight: 400; margin-top: 2px; }
</style>
